import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { useLazyQuery } from '@apollo/client';
import { GET_PRODUCT } from '~/services/store';
import { ReactComponent as NoImageIcon } from '~/assets/svg/no_imagen.svg';

// Interface para el producto basada en la nueva estructura
interface Product {
  id?: string;
  title: string;
  totalInventory: number;
  description: string;
  onlineStoreUrl?: string;
  onlineStorePreviewUrl?: string;
  mainImageURL?: string;
  videoUrl?: string;
  priceRangeV2: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
    maxVariantPrice?: {
      amount: string;
      currencyCode: string;
    };
  };
  variants?: Variant[];
}

interface Variant {
  id: string;
  title: string;
  imageURL?: string;
  selectedOptions: SelectedOption[];
  price: string;
  currency?: string;
  inventory?: number;
}

interface SelectedOption {
  name: string;
  value: string;
}

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [t] = useTranslation('store');
  const [product, setProduct] = useState<Product | null>(null);
  const { agentId } = useParams();

  // Helper function to convert YouTube URL to embed URL
  const getEmbedUrl = (url: string) => {
    // YouTube URL patterns
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(youtubeRegex);
    
    if (match) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
    
    // For other video URLs, return as is
    return url;
  };

  // Helper function to format price with currency
  const formatPrice = (amount: string, currencyCode?: string) => {
    // Si no hay moneda o amount, mostrar raya
    if (!amount || amount === '0' || !currencyCode) {
      return '-';
    }
    
    const symbols = {
      COP: '$',
      USD: '$',
      EUR: '€',
      GBP: '£',
      CNY: '¥'
    };
    
    const symbol = symbols[currencyCode as keyof typeof symbols] || '$';
    const locale = currencyCode === 'EUR' ? 'es-ES' : 'es-CO';
    const currencyTranslation = t(`product.currencies.${currencyCode}`);
    const currencyDisplay = currencyTranslation && currencyTranslation !== `product.currencies.${currencyCode}` 
      ? currencyTranslation 
      : currencyCode;
    
    return `${symbol}${parseFloat(amount).toLocaleString(locale)} ${currencyDisplay}`;
  };

  const [getProduct, { loading, error }] = useLazyQuery(GET_PRODUCT, {
    variables: { data: { agent_id: agentId, product_id: id } },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setProduct(data.get_product.data.product);
    },
  });

  useEffect(() => {
    if (id) {
      getProduct();
    }
  }, [id, getProduct]);

  const handleBackClick = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="w-full space-y-6">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="skeleton h-5 w-5 rounded"></div>
            <div className="skeleton h-4 w-32 rounded"></div>
          </div>
          <div className="skeleton h-10 w-24 rounded-lg"></div>
        </div>

        {/* Main content skeleton */}
        <div className="card bg-base-100 border-base-300 border shadow-xl">
          <div className="card-body">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              {/* Image skeleton */}
              <div className="space-y-4">
                <div className="skeleton aspect-square rounded-lg"></div>
              </div>

              {/* Product info skeleton */}
              <div className="space-y-6">
                <div>
                  <div className="skeleton mb-2 h-8 rounded"></div>
                  <div className="skeleton h-6 w-24 rounded"></div>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="skeleton mb-2 h-6 w-20 rounded"></div>
                    <div className="space-y-2">
                      <div className="skeleton h-4 rounded"></div>
                      <div className="skeleton h-4 rounded"></div>
                      <div className="skeleton h-4 w-3/4 rounded"></div>
                    </div>
                  </div>

                  <div>
                    <div className="skeleton mb-2 h-6 w-32 rounded"></div>
                    <div className="skeleton h-4 w-40 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Variants skeleton */}
        <div className="card bg-base-100 border-base-300 border shadow-xl">
          <div className="card-body">
            <div className="skeleton mb-6 h-6 w-40 rounded"></div>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="border-base-300 flex items-center gap-4 rounded border p-4"
                >
                  <div className="skeleton h-16 w-16 rounded-lg"></div>
                  <div className="flex-1">
                    <div className="skeleton mb-2 h-4 rounded"></div>
                    <div className="skeleton h-4 w-20 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product && !loading) {
    return (
      <div className="w-full space-y-4">
        <button onClick={handleBackClick} className="btn btn-ghost gap-2">
          <ArrowLeftIcon className="h-5 w-5" />
          Volver al inventario
        </button>
        <div className="hero min-h-64 bg-base-100 border-base-300 rounded-lg border">
          <div className="hero-content text-center">
            <div className="max-w-md">
              <p className="text-base-content">
                {error ? t('loading_error') : t('product_not_found')}
              </p>
              {error && (
                <div className="alert alert-error mt-4">
                  <span>{error.message}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Header con botón de regresar */}
      <div className="flex items-center justify-between">
        <button onClick={handleBackClick} className="btn btn-ghost gap-2">
          <ArrowLeftIcon className="h-5 w-5" />
          {t('back_to_inventory')}
        </button>

        <a
          href={product?.onlineStorePreviewUrl || '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary gap-2"
        >
          {t('view_in_store')}
          <ArrowTopRightOnSquareIcon className="h-5 w-5" />
        </a>
      </div>

      {/* Contenido principal */}
      <div className="card bg-base-100 border-base-300 border shadow-xl">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {/* Imagen del producto */}
            <div className="space-y-4">
              <div className="bg-base-200 border-base-300 aspect-square overflow-hidden rounded-lg border">
                {product?.mainImageURL ? (
                  <img
                    src={product.mainImageURL}
                    alt={product?.title || 'Product'}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <NoImageIcon className="text-base-content/50 h-full w-full object-cover" />
                )}
              </div>
            </div>

            {/* Información del producto */}
            <div className="space-y-6">
              <div>
                <h1 className="card-title text-base-content mb-2 text-3xl">
                  {product?.title}
                </h1>
                <div className="text-success text-xl font-bold">
                  {formatPrice(
                    product?.priceRangeV2?.minVariantPrice?.amount || '0',
                    product?.priceRangeV2?.minVariantPrice?.currencyCode
                  )}
                  {product?.priceRangeV2?.maxVariantPrice?.amount !==
                    product?.priceRangeV2?.minVariantPrice?.amount && (
                    <span>
                      {' '}
                      - {formatPrice(
                        product?.priceRangeV2?.maxVariantPrice?.amount || '0',
                        product?.priceRangeV2?.maxVariantPrice?.currencyCode
                      )}
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-base-content mb-2 text-lg font-semibold">
                    {t('product.description')}
                  </h3>
                  <p className="text-base-content leading-relaxed">
                    {product?.description}
                  </p>
                </div>

                {/* Video section */}
                {product?.videoUrl && (
                  <div>
                    <h3 className="text-base-content mb-2 text-lg font-semibold">
                      {t('product.video')}
                    </h3>
                    <div className="aspect-video">
                      <iframe
                        src={getEmbedUrl(product.videoUrl)}
                        title={t('product.video')}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="h-full w-full rounded-lg"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="text-base-content mb-2 text-lg font-semibold">
                    {t('product.available_inventory')}
                  </h3>
                  <div
                    className={`badge badge-lg ${
                      (product?.totalInventory || 0) > 0
                        ? 'badge-success'
                        : 'badge-error'
                    }`}
                  >
                    {(product?.totalInventory || 0) > 0
                      ? `${product?.totalInventory} ${t(
                          'product.units_in_stock',
                        )}`
                      : t('product.out_of_stock')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Variantes adicionales del producto */}
      {product?.variants && product.variants.length > 1 && (
        <div className="card bg-base-100 border-base-300 border shadow-xl">
          <div className="card-body">
            <h3 className="card-title text-base-content mb-4">
              Variantes adicionales
            </h3>
            

            {/* Tabla de variantes */}
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr className="border-base-300">
                    <th className="bg-base-200 text-base-content">
                      {t('table.image')}
                    </th>
                    <th className="bg-base-200 text-base-content">
                      {t('table.name')}
                    </th>
                    <th className="bg-base-200 text-base-content">Precio</th>
                    <th className="bg-base-200 text-base-content">
                      Inventario
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {product?.variants &&
                    product.variants
                      .slice(1)
                      .map((variant: Variant, index: number) => (
                        <tr
                          key={variant.id || index}
                          className="hover:bg-base-200 border-base-300 bg-base-100"
                        >
                          {/* Imagen */}
                          <td>
                            <div className="avatar">
                              <div className="mask mask-squircle h-16 w-16">
                                {variant.imageURL || product?.mainImageURL ? (
                                  <img
                                    src={
                                      variant.imageURL || product?.mainImageURL
                                    }
                                    alt={
                                      variant.title ||
                                      product?.title ||
                                      'Product variant'
                                    }
                                  />
                                ) : (
                                  <div className="bg-base-200 flex h-full w-full items-center justify-center">
                                    <NoImageIcon className="text-base-content/50 h-8 w-8" />
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Nombre */}
                          <td>
                            <div className="text-base-content font-bold">
                              {variant.title ||
                                variant.selectedOptions?.[0]?.value ||
                                product.title}
                            </div>
                            {variant.selectedOptions &&
                              variant.selectedOptions.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {variant.selectedOptions.map(
                                    (option: any, idx: number) => (
                                      <div
                                        key={idx}
                                        className="badge badge-outline badge-sm"
                                      >
                                        {option.name}: {option.value}
                                      </div>
                                    ),
                                  )}
                                </div>
                              )}
                          </td>

                          {/* Precio */}
                          <td>
                            <div className="text-success font-bold">
                              {formatPrice(
                                variant.price,
                                variant.currency || product?.priceRangeV2?.minVariantPrice?.currencyCode
                              )}
                            </div>
                          </td>

                          {/* Inventario */}
                          <td>
                            <div
                              className={`badge ${
                                (variant.inventory || 0) > 0
                                  ? 'badge-success'
                                  : 'badge-error'
                              }`}
                            >
                              {variant.inventory || 0} unidades
                            </div>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
